/* eslint-disable react/jsx-newline */

import * as React from "react";
import styled from "styled-components";

import type { TabSetting } from "../../../../../../../react-components/lu-component/src/index";
import { TabViewer } from "../../../../../../../react-components/lu-component/src/index";
import { AdCopyRole } from "../../../../../../../server/lib/ad-copy/permission/role";
import type { AccountWithoutPassword } from "../../../../../../../server/models/account";
import {
	adCopyConceptTypeMapForQuota,
	type AdCopyTestOnlyConceptKey,
} from "../../../../../../../server/models/ad-copy/concept";
import type { AdCopyCorrectionValue } from "../../../../../../../server/models/ad-copy/simulation-value";
import type { ErrorObject } from "../../../../../../../server/types/error";
import type { GetAdCopySimulationResponse } from "../../../../../../../server/types/request/ad-copy/report/simulation";
import { AdCopyReportBigTitle } from "../parts/report-title";
import type { AdCopyCPPageProps } from "./cp";
import { AdCopyCPPage } from "./cp";
import { AdCopySimulationOne } from "./simulation-one";

const AreaWrap = styled.div`
	width: 1500px;
	margin: 0px auto;
	padding-left: 5px;
`;

const AlertRed = styled.span`
	display: inline-block;
	color: red;
	font-size: 12px;
`;

export type AdCopySimulationPageProps = GetAdCopySimulationResponse & {
	confirmMessage: string | undefined;
	errors: ErrorObject | undefined;
	loginAccount: AccountWithoutPassword;
	onChangeCP: AdCopyCPPageProps["onChange"];
	onChangeRatio: <K extends keyof AdCopyCorrectionValue>(
		target: AdCopyTestOnlyConceptKey,
		name: K,
		value: AdCopyCorrectionValue[K],
	) => void;
	onClearCP: () => void;
	onSimulation: (conceptType: AdCopyTestOnlyConceptKey) => void;
	onSimulationSave: (conceptType: AdCopyTestOnlyConceptKey) => void;
	onSubmitCP: AdCopyCPPageProps["onSubmit"];
	onSubmitCPCorrectionValues: AdCopyCPPageProps["onSubmitCPCorrectionValues"];
	refetch: () => void;
	setChanged: (changed: boolean) => void;
};

export const AdCopySimulationPage = React.memo((props: AdCopySimulationPageProps) => {
	const {
		activity,
		confirmMessage,
		constValue,
		cp,
		currentProductPurchaseRate,
		errors,
		evaluation,
		goalSalesVolume,
		hasCurrentProduct,
		job,
		loginAccount,
		maxRecognition,
		onChangeCP,
		onChangeRatio,
		onClearCP,
		onSimulation,
		onSimulationSave,
		onSubmitCP,
		onSubmitCPCorrectionValues,
		onsByPurchaseIntent,
		refetch,
		setChanged,
		simulationPredictionData,
		trialSalesVolumeBeforeCorrectionByCurrentProduct,
	} = props;

	const hasEditRole = React.useMemo(
		() =>
			// 管理者 or ブランドメンバー or ブランドマネージャーは対象
			AdCopyRole.isAdmin(loginAccount) ||
			AdCopyRole.isActivityMember(loginAccount, activity) ||
			AdCopyRole.isActivityBrandManager(loginAccount, activity),
		[activity, loginAccount],
	);

	const canChangeCP = job != null;

	const tabs = React.useMemo<TabSetting[]>(() => {
		const tmp: TabSetting[] = [];

		if (simulationPredictionData.test1) {
			const targetConceptType = "test1";

			tmp.push({
				children: (
					<AdCopySimulationOne
						activity={activity}
						constValue={constValue}
						cp={cp}
						currentProductPurchaseRate={currentProductPurchaseRate}
						data={simulationPredictionData[targetConceptType]}
						evaluationResult={evaluation[targetConceptType]}
						goalSalesVolume={goalSalesVolume}
						hasCurrentProduct={hasCurrentProduct}
						hasEditRole={hasEditRole}
						job={job}
						maxRecognition={maxRecognition}
						onChangeRatio={(name, value) => {
							onChangeRatio(targetConceptType, name, value);
						}}
						onSimulation={() => {
							onSimulation(targetConceptType);
						}}
						onSimulationSave={() => {
							onSimulationSave(targetConceptType);
						}}
						onsData={onsByPurchaseIntent[targetConceptType]}
						targetConceptType={targetConceptType}
						trialSalesVolumeBeforeCorrectionByCurrentProduct={trialSalesVolumeBeforeCorrectionByCurrentProduct}
					/>
				),
				key: targetConceptType,
				title: adCopyConceptTypeMapForQuota[targetConceptType],
			});
		}

		if (simulationPredictionData.test2 && evaluation.test2 && onsByPurchaseIntent.test2) {
			const targetConceptType = "test2";

			tmp.push({
				children: (
					<AdCopySimulationOne
						activity={activity}
						constValue={constValue}
						cp={cp}
						currentProductPurchaseRate={currentProductPurchaseRate}
						data={simulationPredictionData[targetConceptType]}
						evaluationResult={evaluation[targetConceptType]}
						goalSalesVolume={goalSalesVolume}
						hasCurrentProduct={hasCurrentProduct}
						hasEditRole={hasEditRole}
						job={job}
						maxRecognition={maxRecognition}
						onChangeRatio={(name, value) => {
							onChangeRatio(targetConceptType, name, value);
						}}
						onSimulation={() => {
							onSimulation(targetConceptType);
						}}
						onSimulationSave={() => {
							onSimulationSave(targetConceptType);
						}}
						onsData={onsByPurchaseIntent[targetConceptType]}
						targetConceptType={targetConceptType}
						trialSalesVolumeBeforeCorrectionByCurrentProduct={trialSalesVolumeBeforeCorrectionByCurrentProduct}
					/>
				),
				key: targetConceptType,
				title: adCopyConceptTypeMapForQuota[targetConceptType],
			});
		}

		if (simulationPredictionData.test3 && evaluation.test3 && onsByPurchaseIntent.test3) {
			const targetConceptType = "test3";

			tmp.push({
				children: (
					<AdCopySimulationOne
						activity={activity}
						constValue={constValue}
						cp={cp}
						currentProductPurchaseRate={currentProductPurchaseRate}
						data={simulationPredictionData[targetConceptType]}
						evaluationResult={evaluation[targetConceptType]}
						goalSalesVolume={goalSalesVolume}
						hasCurrentProduct={hasCurrentProduct}
						hasEditRole={hasEditRole}
						job={job}
						maxRecognition={maxRecognition}
						onChangeRatio={(name, value) => {
							onChangeRatio(targetConceptType, name, value);
						}}
						onSimulation={() => {
							onSimulation(targetConceptType);
						}}
						onSimulationSave={() => {
							onSimulationSave(targetConceptType);
						}}
						onsData={onsByPurchaseIntent[targetConceptType]}
						targetConceptType={targetConceptType}
						trialSalesVolumeBeforeCorrectionByCurrentProduct={trialSalesVolumeBeforeCorrectionByCurrentProduct}
					/>
				),
				key: targetConceptType,
				title: adCopyConceptTypeMapForQuota[targetConceptType],
			});
		}

		if (hasCurrentProduct && canChangeCP) {
			tmp.push({
				children: (
					<AdCopyCPPage
						activity={activity}
						constValue={constValue}
						cp={cp ?? {}}
						errors={errors}
						hasEditRole={hasEditRole}
						job={job}
						maxRecognition={maxRecognition}
						onChange={onChangeCP}
						onClear={onClearCP}
						onSubmit={onSubmitCP}
						onSubmitCPCorrectionValues={onSubmitCPCorrectionValues}
					/>
				),
				key: "cp",
				title: "実績値修正",
			});
		}

		return tmp;
	}, [
		activity,
		canChangeCP,
		constValue,
		cp,
		currentProductPurchaseRate,
		errors,
		evaluation,
		goalSalesVolume,
		hasCurrentProduct,
		hasEditRole,
		job,
		maxRecognition,
		onChangeCP,
		onChangeRatio,
		onClearCP,
		onSimulation,
		onSimulationSave,
		onSubmitCP,
		onSubmitCPCorrectionValues,
		onsByPurchaseIntent,
		simulationPredictionData,
		trialSalesVolumeBeforeCorrectionByCurrentProduct,
	]);

	return (
		<AreaWrap>
			<AdCopyReportBigTitle>
				補整＆提案フォーマット<AlertRed>（補整値は、アクティビティメンバーと承認者のみ入力可能です）</AlertRed>
				{job ? `（${job.jobNum}）` : ""}
			</AdCopyReportBigTitle>

			{tabs.length > 0 && (
				<TabViewer
					changed={!!confirmMessage}
					confirm={{
						body: confirmMessage ?? "",
						title: "保存確認",
					}}
					id="resultByTarget"
					onChangeTab={() => {
						setChanged(false);
						refetch();
					}}
					tabs={tabs}
				/>
			)}
		</AreaWrap>
	);
});
